// Where the document itself starts inside the viewer's pane (#13681): below
// any toolbar a file kind draws across the top of its document, so a bar
// pinned over the document never covers that toolbar's controls.
import { useLayoutEffect, useState } from "react";

/** What counts as a kind's own toolbar across the top of the document. Our
 *  own viewers mark theirs (the PDF reader's reading aids); the spreadsheet's
 *  toolbar and formula bar are the library's DOM, so they are named by the
 *  library's class names here, in the one list. */
const VIEWER_TOOLBAR_SELECTOR =
  "[data-viewer-toolbar], .fortune-toolbar, .fortune-fx-editor";

const toolbarsIn = (pane: HTMLElement): HTMLElement[] =>
  Array.from(pane.querySelectorAll<HTMLElement>(VIEWER_TOOLBAR_SELECTOR));

/** The bottom edge of the lowest of `toolbars`, in px from the pane's top;
 *  0 when the kind draws none. */
function toolbarBottom(pane: HTMLElement, toolbars: HTMLElement[]): number {
  const paneTop = pane.getBoundingClientRect().top;
  let bottom = 0;
  for (const el of toolbars) {
    const rect = el.getBoundingClientRect();
    if (rect.height > 0) bottom = Math.max(bottom, rect.bottom - paneTop);
  }
  return Math.round(bottom);
}

const sameElements = (a: HTMLElement[], b: HTMLElement[]): boolean =>
  a.length === b.length && a.every((el, i) => el === b[i]);

/** `toolbarBottom` for `pane`, kept current as the pane's content changes
 *  (another kind loads, a toolbar mounts) and as the pane or a toolbar
 *  resizes (a pane narrowing folds a toolbar onto two rows). Watches
 *  nothing while `pane` is null. */
export function useViewerToolbarBottom(pane: HTMLElement | null): number {
  const [bottom, setBottom] = useState(0);
  // A layout effect: the bar mounts with nothing measured, and a passive
  // effect would let it paint one frame over a toolbar already drawn.
  useLayoutEffect(() => {
    if (!pane) return undefined;
    // Null until the first measure, which observes the pane itself too.
    let toolbars: HTMLElement[] | null = null;
    const resized = new ResizeObserver(() =>
      setBottom(toolbarBottom(pane, toolbars ?? []))
    );
    const measure = () => {
      const found = toolbarsIn(pane);
      // Re-observe only when the set of toolbars changed: a spreadsheet's
      // cells mutate the pane far more often than its toolbars come and go.
      if (!toolbars || !sameElements(found, toolbars)) {
        toolbars = found;
        resized.disconnect();
        resized.observe(pane);
        for (const el of found) resized.observe(el);
      }
      setBottom(toolbarBottom(pane, found));
    };
    const changed = new MutationObserver(measure);
    changed.observe(pane, { childList: true, subtree: true });
    measure();
    return () => {
      changed.disconnect();
      resized.disconnect();
    };
  }, [pane]);
  return bottom;
}
